import type { TextSizeMode } from '~/design-system/components/DeckGen/types/presentation'

export const getTextWidthAuto = (mode?: TextSizeMode) =>
  mode === 'auto' || mode === 'auto-width' || mode == null

export const getTextHeightAuto = (mode?: TextSizeMode) =>
  mode === 'auto' || mode === 'auto-width' || mode === 'auto-height' || mode == null

export const hasAnyTextAutoSize = (mode?: TextSizeMode) =>
  getTextWidthAuto(mode) || getTextHeightAuto(mode)

export const getTextResizeToolbarValue = (
  mode?: TextSizeMode,
): 'auto-width' | 'auto-height' | 'fixed' => {
  if (mode === 'auto-height' || mode === 'fixed') {
    return mode
  }
  return 'auto-width'
}

// Giving the box an explicit WIDTH fixes the width. If the height is still auto (any non-fixed mode) the
// result is auto-height (fixed width, content height); if the height was already fixed, stay fully fixed —
// editing the width in Fixed mode must NOT silently make the height auto and collapse the box.
export const setWidthFixed = (mode?: TextSizeMode): TextSizeMode => (mode === 'fixed' ? 'fixed' : 'auto-height')

// Giving an explicit HEIGHT fixes the height. There is no "auto-width + fixed-height" mode, so fixing the
// height always lands on fully fixed.
export const setHeightFixed = (_mode?: TextSizeMode): TextSizeMode => 'fixed'

const SIZE_DELTA_THRESHOLD = 1

const isBothAutoMode = (mode?: TextSizeMode) =>
  mode === 'auto' || mode === 'auto-width' || mode == null

export const getTextSizeModeAfterManualResize = (
  start: { width: number; height: number },
  end: { width: number; height: number },
  currentMode?: TextSizeMode,
  resizeDirection?: string,
): TextSizeMode | null => {
  if (currentMode === 'fixed') {
    return null
  }

  const widthChanged = Math.abs(end.width - start.width) > SIZE_DELTA_THRESHOLD
  const heightChanged = Math.abs(end.height - start.height) > SIZE_DELTA_THRESHOLD
  if (!widthChanged && !heightChanged) {
    return null
  }

  const dir = resizeDirection ?? ''
  const isHorizontal = dir === 'left' || dir === 'right'
  const isVertical = dir === 'top' || dir === 'bottom'

  if (isVertical) {
    return 'fixed'
  }

  if (isHorizontal) {
    if (isBothAutoMode(currentMode)) {
      return 'auto-height'
    }
    if (currentMode === 'auto-height') {
      return null
    }
    return 'auto-height'
  }

  if (dir) {
    return 'fixed'
  }

  if (heightChanged && widthChanged) {
    return 'fixed'
  }
  if (heightChanged) {
    return 'fixed'
  }
  if (widthChanged && isBothAutoMode(currentMode)) {
    return 'auto-height'
  }
  if (widthChanged) {
    return currentMode === 'auto-height' ? null : 'auto-height'
  }

  return null
}
